import * as React from 'react'
import OffersApiService, { OfferTranslationOption } from '../../services/OffersApiService'

interface Props {
    offerId: number
    languageId: number
    languages: OfferTranslationOption[]
    onBusyChange: (busy: boolean) => void
    onComplete: (updatedLanguages: number[]) => Promise<void>
}

export default function OfferRetranslateButton({ offerId, languageId, languages, onBusyChange, onComplete }: Props) {
    const [busy, setBusy] = React.useState(false)
    const [status, setStatus] = React.useState('')
    const running = React.useRef(false)
    const generation = React.useRef(0)
    const complete = React.useRef(onComplete)
    complete.current = onComplete
    React.useEffect(() => {
        ++generation.current
        running.current = false
        setBusy(false)
        setStatus('')
        return () => { ++generation.current; onBusyChange(false) }
    }, [offerId])

    // Unknown provenance is not eligible until the server has supplied it.
    const targets = languages.filter(language => Number(language.value) > 0 &&
        language.isExternalTranslation === false && (languageId === 0 || Number(language.value) === languageId))
    const selected = languages.find(language => Number(language.value) === languageId)
    const label = languageId === 0 ? 'Retranslate to all languages' : `Retranslate to ${selected?.label || 'selected language'}`
    const title = selected?.isExternalTranslation ? 'External supplier translations cannot be retranslated' :
        targets.length ? label : 'No internal translations to refresh'

    const retranslate = async () => {
        if (running.current || targets.length === 0) return
        const current = generation.current
        const isCurrent = () => current === generation.current
        running.current = true
        setBusy(true)
        onBusyChange(true)
        const updated: number[] = []
        const failed: string[] = []
        let skipped = 0
        try {
            // One language per request keeps progress visible and bounds each request's work.
            for (const language of targets) {
                if (!isCurrent()) return
                setStatus(`Translating ${language.label} (${updated.length + failed.length + skipped + 1}/${targets.length})…`)
                try {
                    const response = await OffersApiService.retranslateOffer(offerId, Number(language.value))
                    if (!isCurrent()) return
                    if (!response.IsOk || !response.Content) throw new Error(response.ErrorMessage || 'Translation failed')
                    if (response.Content.updated) updated.push(Number(language.value))
                    else skipped++
                } catch (error) {
                    if (!isCurrent()) return
                    failed.push(`${language.label}: ${error instanceof Error ? error.message : 'Translation failed'}`)
                }
            }
            if (!isCurrent()) return
            setStatus(`${updated.length} updated${skipped ? `, ${skipped} skipped (external or removed)` : ''}.${failed.length ? ` Failed: ${failed.join('; ')}` : ''}`)
            await complete.current(updated)
        } catch (error) {
            if (isCurrent()) setStatus(`Translations processed, but the preview could not be refreshed. Reload the page.`)
        } finally {
            if (isCurrent()) {
                running.current = false
                setBusy(false)
                onBusyChange(false)
            }
        }
    }

    if (languageId < 0) return null
    return <div style={{ maxWidth: 320 }}>
        <button type='button' className='button' title={title} aria-label={label}
            disabled={busy || !offerId || targets.length === 0} onClick={retranslate}>
            <i className={`fa fa-refresh${busy ? ' fa-spin' : ''}`} aria-hidden='true' />
        </button>
        {status && <div role='status' aria-live='polite' style={{ fontSize: 12, marginTop: 5 }}>{status}</div>}
    </div>
}
